import {createPortal} from 'react-dom'
import {DragAndDrop} from '@github-ui/drag-and-drop'
import React, {useCallback} from 'react'
import {getLineInMarkdown, updateLocalState, updateMarkdown, type TaskItem, type onDropProps} from '../use-tasklist'
import styles from './TaskListItems.module.css'
import {TaskListItem} from './TaskListItem'
import {Box} from '@primer/react'

export type TaskListItemsProps = {
  tasklists: Element[]
  markdownValue: string
  tasklistData: Map<Element, TaskItem[]>
  setTasklistData: React.Dispatch<React.SetStateAction<Map<Element, TaskItem[]>>>
  onConvertToIssue?: (task: TaskItem, setIsConverting: (converting: boolean) => void) => void
  externalOnChange?: (value: string) => void | Promise<void>
  nestedItems: Map<string, number | undefined>
  disabled?: boolean
}

export const TaskListItems = ({
  tasklists,
  markdownValue,
  externalOnChange,
  onConvertToIssue,
  tasklistData,
  setTasklistData,
  disabled,
}: TaskListItemsProps) => {
  const onDrop = useCallback(
    ({dragMetadata, dropMetadata, isBefore, items, container}: onDropProps) => {
      if (dragMetadata.id === dropMetadata?.id || disabled) return

      // item that is being dropped
      const dropIndexItem = items.find(item => item.id === dropMetadata.id)

      const droppedChildren = (dropIndexItem && dropIndexItem.totalNestedItems) ?? 0

      // index of the item being dragged
      const dragIndex = Number(items.find(item => item.id === dragMetadata.id)?.markdownIndex)

      // index of the item being dropped
      const dropIndex = Number(dropIndexItem?.markdownIndex)

      const dragIndexItem = items.find(item => item.id === dragMetadata.id)

      // total number of nested elements being dragged
      const draggedChildren = (dragIndexItem && dragIndexItem.totalNestedItems) ?? 0

      const dragLineIndex = getLineInMarkdown(dragIndex, markdownValue)
      const dropLineIndex = getLineInMarkdown(dropIndex, markdownValue) + (isBefore ? 0 : droppedChildren)

      if (dragLineIndex >= 0 && dropLineIndex >= 0) {
        const updatedMarkdown = updateMarkdown(markdownValue, dragLineIndex, isBefore, dropLineIndex, draggedChildren)

        const newData = updateLocalState(items, dragMetadata.id, dropMetadata.id, tasklistData, isBefore, container)

        setTasklistData(newData)

        externalOnChange?.(updatedMarkdown)
      }
    },
    [disabled, markdownValue, tasklistData, setTasklistData, externalOnChange],
  )

  function renderNestedItems(
    item: TaskItem,
    position: number,
    container: Element,
    totalItems: number,
    nested: boolean,
    isDragOverlay: boolean,
  ) {
    return (
      <React.Fragment key={item.index}>
        <TaskListItem
          markdownValue={markdownValue}
          onChange={async (value: string) => await externalOnChange?.(value)}
          onConvertToIssue={onConvertToIssue}
          markdownIndex={Number(item.markdownIndex)}
          nested={nested}
          position={position}
          item={item}
          totalItems={totalItems}
          disabled={disabled}
          isDragOverlay={isDragOverlay}
        />

        <DragAndDrop
          items={item.children}
          onDrop={args => onDrop({...args, items: item.children, container})}
          renderOverlay={(child, index) => (
            <DragAndDrop.Item
              index={index}
              id={child.id}
              key={child.id}
              title={child.title}
              isDisabled={true}
              className={styles['task-list-item']}
              isDragOverlay
              style={{
                marginLeft: '-30px',
              }}
            >
              {renderNestedItems(child, position + 1, container, item.children.length, true, true)}
            </DragAndDrop.Item>
          )}
          as={'div'}
        >
          <Box
            as={container.tagName === 'OL' ? 'ol' : container.tagName === 'UL' ? 'ul' : 'div'}
            className={'base-list-item'}
          >
            {item.children.map((child, index) => {
              const orderedUnorderedItem = child.isNumbered
                ? position + 1 < 2
                  ? styles['numbered-task-list']
                  : styles['numbered-task-list-nested']
                : child.isBullet
                  ? position + 1 < 2
                    ? styles['unordered-task-list']
                    : styles['unordered-task-list-nested']
                  : styles['task-list-item']

              const nestedTaskList =
                !child.isBullet &&
                !child.isNumbered &&
                (isDragOverlay
                  ? styles['nested-overlay-list']
                  : !child.parentIsChecklist && !child.hasDifferentListTypes
                    ? styles['task-list-item']
                    : styles['nested-task-list'])
              const taskListChild =
                (child.isBullet || child.isNumbered) &&
                (isDragOverlay
                  ? styles['overlay-task-list-child']
                  : child.parentIsChecklist && position < 1
                    ? styles['task-list-child']
                    : styles['nested-task-list'])
              return (
                <DragAndDrop.Item
                  index={index}
                  id={child.id}
                  key={child.id}
                  title={child.title}
                  isDisabled={true}
                  className={`${orderedUnorderedItem} ${nestedTaskList} ${taskListChild}`}
                  as={
                    (item.isNumbered && !item.hasDifferentListTypes) ||
                    (item.isBullet && !item.hasDifferentListTypes) ||
                    item.hasDifferentListTypes ||
                    item.children.length > 0
                      ? 'li'
                      : 'div'
                  }
                >
                  {renderNestedItems(child, position + 1, container, item.children.length, true, isDragOverlay)}
                </DragAndDrop.Item>
              )
            })}
          </Box>
        </DragAndDrop>
      </React.Fragment>
    )
  }

  return (
    <>
      {tasklists.map((container, i): JSX.Element => {
        const items = tasklistData.get(container) || []
        const checkListItems = items.filter(item => !item.isBullet && !item.isNumbered)
        return createPortal(
          <li className="base-task-list-item" style={{listStyle: 'none', marginLeft: '-32px'}}>
            <DragAndDrop
              items={items}
              onDrop={args => onDrop({...args, items, container})}
              style={{
                marginRight: '-12px',
                marginTop: '1px',
              }}
              renderOverlay={(item, index) => (
                <DragAndDrop.Item
                  index={index}
                  id={item.id}
                  key={item.id}
                  title={item.title}
                  isDisabled={true}
                  isDragOverlay
                >
                  {renderNestedItems(item, 0, container, items.length, false, true)}
                </DragAndDrop.Item>
              )}
              as="div"
            >
              <Box
                as={
                  container.tagName === 'OL'
                    ? 'ol'
                    : container.tagName === 'UL' &&
                        (!container.classList.contains('contains-task-list') || checkListItems.length !== items.length)
                      ? 'ul'
                      : 'div'
                }
                className={'base-list-item'}
              >
                {items.map((item, index) => {
                  return (
                    <DragAndDrop.Item
                      index={index}
                      id={item.id}
                      key={item.id}
                      title={item.title}
                      isDisabled={true}
                      className={
                        !item.isBullet && !item.isNumbered
                          ? !item.nested && !item.hasDifferentListTypes
                            ? styles['task-list-item']
                            : styles['nested-task-list-item']
                          : item.isBullet
                            ? styles['bullet-task-item']
                            : ''
                      }
                      as={
                        (item.isNumbered && !item.hasDifferentListTypes) ||
                        (item.isBullet && !item.hasDifferentListTypes) ||
                        item.hasDifferentListTypes
                          ? 'li'
                          : 'div'
                      }
                    >
                      {renderNestedItems(item, 0, container, items.length, false, false)}
                    </DragAndDrop.Item>
                  )
                })}
              </Box>
            </DragAndDrop>
          </li>,
          container,
          // using index as key is OK here because users can't reorder tasklist, but it's still not great because
          // tasklist order _can_ change if a live update comes in with new Markdown. So we definitely want to add a
          // unique ID per tasklist as well.
          i.toString(),
        )
      })}
    </>
  )
}

try{ TaskListItems.displayName ||= 'TaskListItems' } catch {}